import React, { useState, useRef, KeyboardEvent } from 'react'
import { Send, Paperclip, Smile, Mic } from 'lucide-react'
import type { MessageInputProps } from '../../types'

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  placeholder = 'Type a message...',
  disabled = false,
  threadType,
}) => {
  const [message, setMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim())
      setMessage('')

      // Clear typing indicator
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      setIsTyping(false)
      onTyping?.(false)

      // Focus back on input
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setMessage(newValue)

    // Handle typing indicator
    if (!isTyping && newValue.trim()) {
      setIsTyping(true)
      onTyping?.(true)
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Set new timeout to stop typing
    if (newValue.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
        onTyping?.(false)
      }, 1000)
    } else {
      setIsTyping(false)
      onTyping?.(false)
    }
  }

  // Auto-resize textarea
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`
    }
  }, [message])

  return (
    <div className="border-t border-gray-200 dark:border-border bg-white dark:bg-background px-4 py-3 flex-shrink-0">
      <div className="flex items-end gap-2">
        <div className="flex items-center gap-1">
          <button
            className="w-9 h-9 flex items-center justify-center bg-transparent border-none rounded-full text-gray-600 dark:text-muted-foreground cursor-pointer transition-all duration-200 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-800 dark:hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            title="Attach file"
            disabled={disabled}
          >
            <Paperclip className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            className="w-full min-h-10 max-h-30 px-3 py-2.5 bg-gray-100 dark:bg-muted border border-transparent rounded-3xl resize-none outline-none text-sm leading-5 text-gray-900 dark:text-foreground placeholder:text-gray-400 dark:placeholder:text-muted-foreground transition-all duration-200 focus:bg-white dark:focus:bg-background focus:border-gray-900 dark:focus:border-border disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={placeholder}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            rows={1}
          />
        </div>

        <div className="flex items-center gap-1">
          <button
            className="w-9 h-9 flex items-center justify-center bg-transparent border-none rounded-full text-gray-600 dark:text-muted-foreground cursor-pointer transition-all duration-200 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-800 dark:hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            title="Add emoji"
            disabled={disabled}
          >
            <Smile className="h-5 w-5" />
          </button>

          {threadType === 'phone' && (
            <button
              className="w-9 h-9 flex items-center justify-center bg-transparent border-none rounded-full text-gray-600 dark:text-muted-foreground cursor-pointer transition-all duration-200 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-800 dark:hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
              title="Voice message"
              disabled={disabled}
            >
              <Mic className="h-5 w-5" />
            </button>
          )}

          <button
            className={`w-9 h-9 flex items-center justify-center bg-transparent border-none rounded-full cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed ${
              message.trim()
                ? 'text-gray-900 dark:text-foreground hover:bg-gray-100 dark:hover:bg-muted'
                : 'text-gray-400 dark:text-muted-foreground'
            }`}
            onClick={handleSend}
            disabled={disabled || !message.trim()}
            title="Send message"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
